import { computed, ref, watch, type Ref } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { mediaApi } from '@ck/api-client'

// Encapsula a listagem de mídia de uma OS + download lazy de cada blob (mesmo arquivo
// original, sem redimensionamento) e a exclusão — reaproveitado por ChecklistView (miniaturas
// por item) e FotosView (grade completa) para não duplicar a lógica de blob URLs.
export function useWorkOrderMedia(workOrderId: Ref<string | undefined>) {
  const queryClient = useQueryClient()
  const mediaQuery = useQuery(computed(() => ({
    queryKey: ['media', workOrderId.value],
    queryFn: () => mediaApi.list(workOrderId.value!),
    enabled: !!workOrderId.value
  })))

  const photoUrls = ref<Record<string, string>>({})
  watch(() => mediaQuery.data.value, async (assets, _previous, onCleanup) => {
    const urls: Record<string, string> = {}
    for (const asset of assets ?? []) {
      try { urls[asset.id] = URL.createObjectURL(await mediaApi.fetchFile(asset.id)) } catch { /* ignora falha ao baixar uma foto específica */ }
    }
    photoUrls.value = urls
    onCleanup(() => Object.values(urls).forEach((url) => URL.revokeObjectURL(url)))
  }, { immediate: true })

  const removingId = ref<string | null>(null)
  const removeError = ref(false)
  async function remove(id: string) {
    removingId.value = id
    removeError.value = false
    try {
      await mediaApi.remove(id)
      await queryClient.invalidateQueries({ queryKey: ['media', workOrderId.value] })
    } catch {
      removeError.value = true
    } finally {
      removingId.value = null
    }
  }

  return { mediaQuery, photoUrls, remove, removingId, removeError }
}
